// Lists Module - Redesigned
import React, { useState, useEffect, useRef } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  List, Plus, Mic, MicOff, Check, Trash2, X,
  ShoppingCart, CheckSquare, Share2, Download, ChevronRight,
  FolderOpen, ChevronDown, Search, Pencil
} from 'lucide-react';
import ShareModal from './ShareModal';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LIST_TYPES = [
  { id: 'shopping',   label: 'Lista del súper',       emoji: '🛒', color: 'green' },
  { id: 'tasks',      label: 'Tareas',                emoji: '✅', color: 'blue' },
  { id: 'travel',     label: 'Equipaje / Viaje',      emoji: '✈️', color: 'cyan' },
  { id: 'movies',     label: 'Películas / Series',    emoji: '🎬', color: 'violet' },
  { id: 'books',      label: 'Libros',                emoji: '📚', color: 'yellow' },
  { id: 'work',       label: 'Trabajo',               emoji: '💼', color: 'indigo' },
  { id: 'home',       label: 'Hogar',                 emoji: '🏠', color: 'orange' },
  { id: 'fitness',    label: 'Ejercicio',             emoji: '💪', color: 'pink' },
  { id: 'health',     label: 'Salud / Medicamentos',  emoji: '💊', color: 'red' },
  { id: 'wishlist',   label: 'Lista de deseos',       emoji: '🎁', color: 'rose' },
  { id: 'contacts',   label: 'Contactos',             emoji: '👥', color: 'teal' },
  { id: 'events',     label: 'Eventos',               emoji: '🎉', color: 'amber' },
  { id: 'food',       label: 'Recetas / Comida',      emoji: '🍳', color: 'emerald' },
  { id: 'projects',   label: 'Proyectos',             emoji: '🚀', color: 'purple' },
  { id: 'finance',    label: 'Gastos / Finanzas',     emoji: '💰', color: 'lime' },
  { id: 'other',      label: 'Otros',                 emoji: '📋', color: 'gray' },
];

const TYPE_COLORS = {
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  violet: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  indigo: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  rose: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  teal: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  lime: 'bg-lime-500/20 text-lime-400 border-lime-500/30',
  gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const ListsModule = ({ user }) => {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newList, setNewList] = useState({ name: '', list_type: 'shopping', priority: 'normal' });
  const [newItem, setNewItem] = useState('');
  const [listening, setListening] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [shareModal, setShareModal] = useState({ open: false, list: null });
  const recognitionRef = useRef(null);

  useEffect(() => {
    loadLists();
    setupRecognition();
  }, [user.id]);

  const setupRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-MX';
    r.onresult = (e) => {
      const txt = e.results[0][0].transcript;
      if (selectedList) {
        setNewItem(txt);
      } else {
        setNewList(p => ({ ...p, name: txt }));
        setShowCreate(true);
      }
      setListening(false);
    };
    r.onerror = r.onend = () => setListening(false);
    recognitionRef.current = r;
  };

  const loadLists = async () => {
    try {
      const res = await axios.get(`${API}/lists/v2/${user.id}`);
      setLists(res.data || []);
    } catch (e) {}
  };

  const handleVoice = () => {
    if (!recognitionRef.current) { toast.error('Voz no disponible'); return; }
    if (listening) { recognitionRef.current.stop(); return; }
    recognitionRef.current.start();
    setListening(true);
  };

  const createList = async () => {
    if (!newList.name.trim()) { toast.error('Agrega un nombre'); return; }
    try {
      const res = await axios.post(`${API}/lists/v2?user_id=${user.id}`, { ...newList, items: [] });
      setLists(prev => [...prev, res.data]);
      setNewList({ name: '', list_type: 'shopping', priority: 'normal' });
      setShowCreate(false);
      toast.success('Lista creada');
    } catch { toast.error('Error al crear lista'); }
  };

  const deleteList = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta lista?')) return;
    await axios.delete(`${API}/lists/v2/${id}`);
    setLists(prev => prev.filter(l => l.id !== id));
    if (selectedList?.id === id) setSelectedList(null);
    toast.success('Lista eliminada');
  };

  const addItem = async () => {
    if (!newItem.trim() || !selectedList) return;
    try {
      const updatedItems = [...(selectedList.items || []), { id: Date.now(), content: newItem, completed: false }];
      await axios.put(`${API}/lists/v2/${selectedList.id}`, { ...selectedList, items: updatedItems });
      const updated = { ...selectedList, items: updatedItems };
      setSelectedList(updated);
      setLists(prev => prev.map(l => l.id === selectedList.id ? updated : l));
      setNewItem('');
    } catch { toast.error('Error al agregar'); }
  };

  const toggleItem = async (itemId) => {
    if (!selectedList) return;
    try {
      const updatedItems = selectedList.items.map(item =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      );
      await axios.put(`${API}/lists/v2/${selectedList.id}`, { ...selectedList, items: updatedItems });
      const updated = { ...selectedList, items: updatedItems };
      setSelectedList(updated);
      setLists(prev => prev.map(l => l.id === selectedList.id ? updated : l));
    } catch {}
  };

  const deleteItem = async (itemId) => {
    if (!selectedList) return;
    const updatedItems = selectedList.items.filter(i => i.id !== itemId);
    await axios.put(`${API}/lists/v2/${selectedList.id}`, { ...selectedList, items: updatedItems });
    const updated = { ...selectedList, items: updatedItems };
    setSelectedList(updated);
    setLists(prev => prev.map(l => l.id === selectedList.id ? updated : l));
  };

  const exportList = (list) => {
    const items = list.items || [];
    let content = `# ${list.name}\nTipo: ${getTypeInfo(list.list_type)?.label || list.list_type}\nTotal: ${items.length} elementos\n\n`;
    items.forEach(i => { content += `${i.completed ? '✅' : '⬜'} ${i.content}\n`; });
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `lista-${list.name}-${Date.now()}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Lista exportada');
  };

  const getTypeInfo = (typeId) => LIST_TYPES.find(t => t.id === typeId) || LIST_TYPES[LIST_TYPES.length - 1];

  const filteredLists = lists
    .filter(l => filterType === 'all' || l.list_type === filterType)
    .filter(l => !search || l.name?.toLowerCase().includes(search.toLowerCase()));

  return (
      <>
      <SectionAgent section="lists" />
      <div className="p-4 md:p-6 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <List className="text-teal-400" size={24} />Listas
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">{lists.length} lista{lists.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleVoice}
            className={`p-2.5 rounded-xl transition-colors ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
            {listening ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <Plus size={18} />Nueva Lista
          </button>
        </div>
      </div>

      {/* Search + type filter */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar listas..." className="input-glass pl-9 w-full text-sm" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="input-glass text-sm">
          <option value="all">Todos los tipos</option>
          {LIST_TYPES.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
        </select>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="glass-panel p-5 mb-6 animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Nueva Lista</h3>
            <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={18} className="text-gray-400" /></button>
          </div>
          <div className="flex gap-2 mb-3">
            <input value={newList.name} onChange={e => setNewList(p => ({ ...p, name: e.target.value }))}
              placeholder="Nombre de la lista..." className="input-glass flex-1" />
            <button onClick={handleVoice} className={`px-3 rounded-xl ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
              {listening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Tipo de lista</label>
              <select value={newList.list_type} onChange={e => setNewList(p => ({ ...p, list_type: e.target.value }))} className="input-glass w-full text-sm">
                {LIST_TYPES.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Prioridad</label>
              <select value={newList.priority} onChange={e => setNewList(p => ({ ...p, priority: e.target.value }))} className="input-glass w-full text-sm">
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setShowCreate(false)} className="btn-secondary text-sm">Cancelar</button>
            <button onClick={createList} className="btn-primary text-sm">Crear Lista</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lists sidebar */}
        <div className="space-y-3">
          {filteredLists.length === 0 ? (
            <div className="glass-panel p-10 text-center">
              <List size={40} className="text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Sin listas</p>
              <button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">Crear primera lista</button>
            </div>
          ) : filteredLists.map(list => {
            const typeInfo = getTypeInfo(list.list_type);
            const completed = (list.items || []).filter(i => i.completed).length;
            const total = (list.items || []).length;
            const progress = total > 0 ? (completed / total) * 100 : 0;
            const isSelected = selectedList?.id === list.id;
            return (
              <div key={list.id}
                onClick={() => setSelectedList(isSelected ? null : list)}
                className={`glass-panel p-4 rounded-2xl cursor-pointer transition-all hover:scale-[1.01] ${isSelected ? 'ring-1 ring-red-500/30' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xl">{typeInfo.emoji}</span>
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate text-sm">{list.name}</p>
                      <p className="text-gray-500 text-xs">{typeInfo.label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={e => { e.stopPropagation(); exportList(list); }}
                      className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
                      <Download size={13} className="text-gray-400" />
                    </button>
                    <button onClick={e => { e.stopPropagation(); setShareModal({ open: true, list }); }}
                      className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
                      <Share2 size={13} className="text-gray-400" />
                    </button>
                    <button onClick={e => deleteList(list.id, e)}
                      className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors">
                      <Trash2 size={13} className="text-red-400" />
                    </button>
                  </div>
                </div>
                {total > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>{completed}/{total} completados</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* List detail */}
        <div className="lg:col-span-2">
          {selectedList ? (
            <div className="glass-panel p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getTypeInfo(selectedList.list_type).emoji}</span>
                  <div>
                    <h2 className="text-white font-semibold">{selectedList.name}</h2>
                    <p className="text-gray-500 text-xs">{(selectedList.items || []).filter(i => i.completed).length}/{(selectedList.items || []).length} completados</p>
                  </div>
                </div>
                <button onClick={() => setSelectedList(null)} className="p-1.5 hover:bg-white/10 rounded-lg">
                  <X size={18} className="text-gray-400" />
                </button>
              </div>

              {/* Add item */}
              <div className="flex gap-2 mb-4">
                <input
                  value={newItem}
                  onChange={e => setNewItem(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
                  placeholder="Agregar elemento..."
                  className="input-glass flex-1 text-sm"
                />
                <button onClick={handleVoice}
                  className={`px-3 rounded-xl transition-colors ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                  {listening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
                <button onClick={addItem} className="btn-primary px-4 text-sm">
                  <Plus size={18} />
                </button>
              </div>

              {/* Items */}
              <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
                {(selectedList.items || []).length === 0 ? (
                  <p className="text-gray-600 text-center py-8 text-sm">Lista vacía. Agrega el primer elemento.</p>
                ) : (
                  (selectedList.items || []).map(item => (
                    <div key={item.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 group transition-colors">
                      <button onClick={() => toggleItem(item.id)}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${item.completed ? 'bg-green-500 border-green-500' : 'border-gray-600 hover:border-green-500'}`}>
                        {item.completed && <Check size={11} className="text-white" />}
                      </button>
                      <span className={`flex-1 text-sm ${item.completed ? 'line-through text-gray-500' : 'text-gray-200'}`}>{item.content}</span>
                      <button onClick={() => deleteItem(item.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded-lg transition-all">
                        <X size={13} className="text-red-400" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="glass-panel p-16 rounded-2xl flex flex-col items-center justify-center text-center">
              <FolderOpen size={48} className="text-gray-600 mb-4" />
              <p className="text-gray-500">Selecciona una lista para ver sus elementos</p>
              <button onClick={() => setShowCreate(true)} className="btn-primary mt-4 text-sm">Crear nueva lista</button>
            </div>
          )}
        </div>
      </div>

      <ShareModal isOpen={shareModal.open} onClose={() => setShareModal({ open: false, list: null })}
        itemType="list" itemId={shareModal.list?.id} itemTitle={shareModal.list?.name} itemData={{ userId: user.id }} />

      <AIAgentModule sectionContext="listas y tareas" compact={true} placeholder="Crea una lista o pregunta sobre tus tareas..." />
    </div>
  
      </>
    );
};

export default ListsModule;
